import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Badge } from '@/components/ui/badge';

interface AnswerRendererProps {
  answer: string;
  className?: string;
}

/**
 * Enhanced Answer Renderer
 *
 * Renders generated answers with:
 * - Full markdown support (headers, lists, tables)
 * - Visual treatment for product names (bold text with badges)
 * - Styled comparison tables
 * - Proper spacing and typography
 */
export function AnswerRenderer({ answer, className = '' }: AnswerRendererProps) {
  return (
    <div className={`prose prose-sm dark:prose-invert max-w-none ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Enhanced table styling
          table: ({ node, ...props }) => (
            <div className="overflow-x-auto my-4">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg" {...props} />
            </div>
          ),
          thead: ({ node, ...props }) => (
            <thead className="bg-gray-50 dark:bg-gray-800" {...props} />
          ),
          th: ({ node, ...props }) => (
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-700" {...props} />
          ),
          td: ({ node, ...props }) => (
            <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 border-b dark:border-gray-700" {...props} />
          ),
          tr: ({ node, ...props }) => (
            <tr className="hover:bg-gray-50 dark:hover:bg-gray-800" {...props} />
          ),

          // Enhanced headers
          h2: ({ node, ...props }) => (
            <h2 className="text-xl font-bold mt-6 mb-3 text-gray-900 dark:text-gray-100 border-b dark:border-gray-700 pb-2" {...props} />
          ),
          h3: ({ node, ...props }) => (
            <h3 className="text-lg font-semibold mt-4 mb-2 text-gray-800 dark:text-gray-200" {...props} />
          ),

          // Enhanced lists
          ul: ({ node, ...props }) => (
            <ul className="space-y-1 my-3" {...props} />
          ),
          ol: ({ node, ...props }) => (
            <ol className="space-y-1 my-3" {...props} />
          ),
          li: ({ node, ...props }) => (
            <li className="text-sm text-gray-700 dark:text-gray-300" {...props} />
          ),

          // Enhanced paragraphs
          p: ({ node, ...props }) => (
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed my-2" {...props} />
          ),

          // Product names (bold text) with visual treatment
          strong: ({ node, children, ...props }) => {
            const text = String(children);

            // Check if this looks like a product name (contains brand/model info)
            const isProductName = text.length > 10 && (
              text.includes('Michelin') ||
              text.includes('Maxxis') ||
              text.includes('Cubierta') ||
              text.includes('MTB') ||
              text.includes('Racing') ||
              text.includes('Tubeless')
            );

            if (isProductName) {
              return (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-50 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-800">
                  <strong className="text-blue-900 dark:text-blue-200 font-semibold" {...props}>{children}</strong>
                </span>
              );
            }

            return <strong className="font-semibold text-gray-900 dark:text-gray-100" {...props}>{children}</strong>;
          },

          // Code blocks (for technical specs if needed)
          // react-markdown 9+ dropped the `inline` prop. A fenced block carries a
          // language-* className or spans lines; inline code does neither.
          code: ({ node, className, children, ...props }) => {
            const isInline = !/language-/.test(className || '') && !String(children).includes('\n');
            if (isInline) {
              return <code className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 rounded text-xs font-mono text-gray-900 dark:text-gray-100" {...props}>{children}</code>;
            }
            // Keep the language-* class react-markdown supplies next to the styling; before
            // #6308 the spread let it REPLACE the styling class on tagged fences.
            return <code className={`block p-3 bg-gray-100 dark:bg-gray-800 rounded-lg text-xs font-mono overflow-x-auto text-gray-900 dark:text-gray-100 ${className ?? ''}`.trim()} {...props}>{children}</code>;
          },

          // Blockquotes (for important notes)
          blockquote: ({ node, ...props }) => (
            <blockquote className="border-l-4 border-blue-500 pl-4 py-2 my-3 bg-blue-50 dark:bg-blue-900/30 rounded-r" {...props} />
          ),
        }}
      >
        {answer}
      </ReactMarkdown>
    </div>
  );
}
